import { useCallback, useEffect, useReducer, ReactElement, useMemo } from 'react';
import { CrudMode } from '@ohpen/enso-ui';
import { useTranslation } from 'react-i18next';

import { usePmpFetch } from '@pmp/hooks';
import { useValueListsStore } from '@pmp/stores';
import {
  ActionEntityType,
  ProcFeeAssignmentFeResponse,
  procurationFeeAssignmentsCreateDeleteCr,
  procurationFeeAssignmentsValidateDelete,
  DraftProcFeeAssignmentFe,
  ActionType,
  procurationFeeAssignmentsCreateChangeRequest,
  procurationFeeAssignmentsCreateReorderChangeRequest,
} from '@pmp/api';
import {
  ConfirmCrDeleteModal,
  ProcurationFeeAssignmentsCrudForm,
  ProcurationFeeAssignmentsList,
  PROCURATION_FEES_ENTITY_LIST_NAMES,
  procurationFeeAssignmentsColumns,
} from '@pmp/components';
import { crudToActionMap } from '@pmp/utils';

import { reducer, getInitialState } from '../reducer';

export const LenderProcurationFeesSection = (): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('procurationFeeAssignmentsGetAllDefaultProcFeeAssignments');
  const { valueLists, getValueLists } = useValueListsStore();
  const [state, dispatch] = useReducer(reducer<ProcFeeAssignmentFeResponse>, getInitialState<ProcFeeAssignmentFeResponse>());
  const { t } = useTranslation();

  useEffect(() => {
    getValueLists({ alwaysFetch: PROCURATION_FEES_ENTITY_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: ProcFeeAssignmentFeResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: ProcFeeAssignmentFeResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: ProcFeeAssignmentFeResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const handleSubmitDraftProcFeeAssignment = useCallback((values: DraftProcFeeAssignmentFe, mode: CrudMode) => {
    const data = {
      id: values.id,
      order: values.order,
      procurationFeeId: values.procurationFeeId,
      procurationFeeRuleId: values.procurationFeeRuleId,
    };
    return procurationFeeAssignmentsCreateChangeRequest({
      body: {
        data,
        actionType: crudToActionMap[mode] as ActionType,
      },
    });
  }, []);

  const handleReorder = (newOrder: ProcFeeAssignmentFeResponse[]) => {
    const data = newOrder?.map((item: ProcFeeAssignmentFeResponse, index: number) => ({
      id: item?.id,
      procurationFeeId: item?.procurationFee?.key ?? undefined,
      procurationFeeRuleId: item?.procurationFeeRule?.key ?? undefined,
      order: index + 1,
      lockedByActionId: item?.lockedByActionId,
    }));

    return procurationFeeAssignmentsCreateReorderChangeRequest({
      body: {
        data,
        actionType: ActionType.REORDER,
      },
    });
  };

  const initialValues: DraftProcFeeAssignmentFe | null = useMemo(() => {
    if (!state.crud.record) return null;
    const { procurationFee, procurationFeeRule, ...data } = state.crud.record;
    return {
      ...data,
      procurationFeeId: procurationFee?.key ?? undefined,
      procurationFeeRuleId: procurationFeeRule?.key ?? undefined,
    };
  }, [state.crud.record]);

  return (
    <>
      <ProcurationFeeAssignmentsList
        procurationFees={data}
        loading={isFetching}
        valueLists={valueLists}
        parentEntityType={ActionEntityType.LENDERS}
        columns={procurationFeeAssignmentsColumns}
        enableRowDnD
        onRowDnDEnd={handleReorder}
        onCreate={onCreate}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        refreshTable={fetchData}
        permissionKey="lenders"
      />
      {state.crud.open && state.crud.mode && (
        <ProcurationFeeAssignmentsCrudForm
          mode={state.crud.mode}
          valueLists={valueLists}
          initialValues={initialValues}
          refreshTable={fetchData}
          onClose={onCloseCrud}
          submitChangeRequest={handleSubmitDraftProcFeeAssignment}
        />
      )}
      <ConfirmCrDeleteModal
        open={state.deleteModal.open}
        records={state.deleteModal.record ? [state.deleteModal.record] : []}
        idKey="id"
        nameKey="id"
        entityLabel={t('lender.procurationFees.labels.procurationFee')}
        entityLabelPlural={t('lender.procurationFees.labels.procurationFees')}
        getPrecheck={procurationFeeAssignmentsValidateDelete}
        createDeleteChangeRequest={procurationFeeAssignmentsCreateDeleteCr}
        refreshTable={fetchData}
        onClose={onCloseDeleteModal}
      />
    </>
  );
};
